import {
  Code,
  Shuffle,
  Globe,
  FileText,
  Image,
  Calculator,
  Shield,

  Braces,
  Hash,
  Link,
  Palette,
  Clock,
  Key,
  QrCode,
  Edit,
  Search,
  MapPin,
  Lock,
  Dices,
  Code2,
  Camera,
  Archive,
  GitCompare,
  Type,
  Volume2,
  BarChart3,
  Calendar,
  Database,
  Minimize2,
  Crop,
  Droplets,
  Network,
  Activity,
  Server,
  Percent,
  Timer,
  Baby,
  Briefcase,
  FileSpreadsheet,
  FileCode,
  FileCode2,
  Layers,
  Zap
} from 'lucide-react';

export interface Tool {
  id: string;
  name: string;
  description: string;
  longDescription: string;
  icon: any;
  category: string;
  path: string;
  featured?: boolean;
  keywords: string;
  // 兼容旧格式和新格式
  useCases: string | string[];
  benefits: string | string[];
  keyFeatures?: string[];
  proTips?: string[];
  // SEO优化新增字段
  usageGuide?: string[];
  faq?: Array<{question: string; answer: string}>;
  tutorial?: {
    steps: string[];
    examples: Array<{title: string; input: string; output: string}>;
  };
  seoKeywords?: string[];
}

export interface Category {
  id: string;
  name: string;
  description: string;
  icon: any;
  color: string;
  tools: Tool[];
}

export const categories: Category[] = [
  // 开发者工具
  {
    id: 'developer',
    name: 'categories.developer',
    description: 'categories.developer.description',
    icon: Code,
    color: 'blue',
    tools: [
      {
        id: 'json-formatter',
        name: 'tools.jsonFormatter.name',
        description: 'tools.jsonFormatter.description',
        longDescription: 'tools.jsonFormatter.longDescription',
        icon: Braces,
        category: 'developer',
        path: '/tools/developer/json-formatter',
        featured: true,
        keywords: 'tools.jsonFormatter.keywords',
        useCases: [
          'tools.jsonFormatter.useCases.apiDevelopment',
          'tools.jsonFormatter.useCases.dataValidation',
          'tools.jsonFormatter.useCases.configFiles',
          'tools.jsonFormatter.useCases.debugging',
          'tools.jsonFormatter.useCases.documentation'
        ],
        benefits: [
          'tools.jsonFormatter.benefits.improveReadability',
          'tools.jsonFormatter.benefits.errorDetection',
          'tools.jsonFormatter.benefits.timeEfficient',
          'tools.jsonFormatter.benefits.standardCompliant'
        ],
        keyFeatures: [
          'tools.jsonFormatter.features.syntaxHighlighting',
          'tools.jsonFormatter.features.errorValidation',
          'tools.jsonFormatter.features.minifyOption',
          'tools.jsonFormatter.features.copyToClipboard',
          'tools.jsonFormatter.features.fileUpload',
          'tools.jsonFormatter.features.downloadFormatted'
        ],
        proTips: [
          'tools.jsonFormatter.tips.validateFirst',
          'tools.jsonFormatter.tips.useMinify',
          'tools.jsonFormatter.tips.checkNesting',
          'tools.jsonFormatter.tips.backupOriginal'
        ],
        usageGuide: [
          'tools.jsonFormatter.guide.step1',
          'tools.jsonFormatter.guide.step2',
          'tools.jsonFormatter.guide.step3',
          'tools.jsonFormatter.guide.step4',
          'tools.jsonFormatter.guide.step5'
        ],
        faq: [
          {question: 'tools.jsonFormatter.faq.q1', answer: 'tools.jsonFormatter.faq.a1'},
          {question: 'tools.jsonFormatter.faq.q2', answer: 'tools.jsonFormatter.faq.a2'},
          {question: 'tools.jsonFormatter.faq.q3', answer: 'tools.jsonFormatter.faq.a3'},
          {question: 'tools.jsonFormatter.faq.q4', answer: 'tools.jsonFormatter.faq.a4'},
          {question: 'tools.jsonFormatter.faq.q5', answer: 'tools.jsonFormatter.faq.a5'}
        ],
        tutorial: {
          steps: [
            'tools.jsonFormatter.tutorial.step1',
            'tools.jsonFormatter.tutorial.step2',
            'tools.jsonFormatter.tutorial.step3',
            'tools.jsonFormatter.tutorial.step4'
          ],
          examples: [
            {
              title: 'tools.jsonFormatter.examples.basic.title',
              input: 'tools.jsonFormatter.examples.basic.input',
              output: 'tools.jsonFormatter.examples.basic.output'
            },
            {
              title: 'tools.jsonFormatter.examples.nested.title',
              input: 'tools.jsonFormatter.examples.nested.input',
              output: 'tools.jsonFormatter.examples.nested.output'
            }
          ]
        },
        seoKeywords: ['JSON formatter online', 'JSON validator', 'JSON beautifier', 'format JSON', 'JSON pretty print', 'JSON minifier']
      },
      {
        id: 'base64-encoder',
        name: 'tools.base64Encoder.name',
        description: 'tools.base64Encoder.description',
        longDescription: 'tools.base64Encoder.longDescription',
        icon: Code2,
        category: 'developer',
        path: '/tools/developer/base64-encoder',
        featured: true,
        keywords: 'tools.base64Encoder.keywords',
        useCases: [
          'tools.base64Encoder.useCases.dataTransmission',
          'tools.base64Encoder.useCases.imageEmbedding',
          'tools.base64Encoder.useCases.apiAuthentication',
          'tools.base64Encoder.useCases.emailAttachments',
          'tools.base64Encoder.useCases.configStorage'
        ],
        benefits: [
          'tools.base64Encoder.benefits.universalSupport',
          'tools.base64Encoder.benefits.safeTransmission',
          'tools.base64Encoder.benefits.fileSupport',
          'tools.base64Encoder.benefits.batchProcessing'
        ],
        keyFeatures: [
          'tools.base64Encoder.features.textEncoding',
          'tools.base64Encoder.features.fileUpload',
          'tools.base64Encoder.features.batchProcessing',
          'tools.base64Encoder.features.copyToClipboard',
          'tools.base64Encoder.features.downloadResult',
          'tools.base64Encoder.features.urlSafeEncoding'
        ],
        proTips: [
          'tools.base64Encoder.tips.checkPadding',
          'tools.base64Encoder.tips.urlSafeOption',
          'tools.base64Encoder.tips.largeFiles',
          'tools.base64Encoder.tips.validateOutput'
        ],
        usageGuide: [
          'tools.base64Encoder.guide.step1',
          'tools.base64Encoder.guide.step2',
          'tools.base64Encoder.guide.step3',
          'tools.base64Encoder.guide.step4',
          'tools.base64Encoder.guide.step5'
        ],
        faq: [
          {question: 'tools.base64Encoder.faq.q1', answer: 'tools.base64Encoder.faq.a1'},
          {question: 'tools.base64Encoder.faq.q2', answer: 'tools.base64Encoder.faq.a2'},
          {question: 'tools.base64Encoder.faq.q3', answer: 'tools.base64Encoder.faq.a3'},
          {question: 'tools.base64Encoder.faq.q4', answer: 'tools.base64Encoder.faq.a4'},
          {question: 'tools.base64Encoder.faq.q5', answer: 'tools.base64Encoder.faq.a5'}
        ],
        tutorial: {
          steps: [
            'tools.base64Encoder.tutorial.step1',
            'tools.base64Encoder.tutorial.step2',
            'tools.base64Encoder.tutorial.step3',
            'tools.base64Encoder.tutorial.step4'
          ],
          examples: [
            {
              title: 'tools.base64Encoder.examples.text.title',
              input: 'tools.base64Encoder.examples.text.input',
              output: 'tools.base64Encoder.examples.text.output'
            },
            {
              title: 'tools.base64Encoder.examples.url.title',
              input: 'tools.base64Encoder.examples.url.input',
              output: 'tools.base64Encoder.examples.url.output'
            }
          ]
        },
        seoKeywords: ['Base64 encoder online', 'Base64 decoder', 'encode Base64', 'decode Base64', 'Base64 converter', 'file to Base64']
      },
      {
        id: 'url-encoder',
        name: 'tools.urlEncoder.name',
        description: 'tools.urlEncoder.description',
        longDescription: 'tools.urlEncoder.longDescription',
        icon: Link,
        category: 'developer',
        path: '/tools/developer/url-encoder',
        keywords: 'tools.urlEncoder.keywords',
        useCases: [
          'tools.urlEncoder.useCases.webDevelopment',
          'tools.urlEncoder.useCases.apiIntegration',
          'tools.urlEncoder.useCases.formSubmission',
          'tools.urlEncoder.useCases.dataTransmission',
          'tools.urlEncoder.useCases.searchQueries'
        ],
        benefits: [
          'tools.urlEncoder.benefits.safeTransmission',
          'tools.urlEncoder.benefits.standardCompliant',
          'tools.urlEncoder.benefits.crossPlatform',
          'tools.urlEncoder.benefits.errorPrevention'
        ],
        keyFeatures: [
          'tools.urlEncoder.features.bidirectionalEncoding',
          'tools.urlEncoder.features.specialCharacters',
          'tools.urlEncoder.features.batchProcessing',
          'tools.urlEncoder.features.copyToClipboard',
          'tools.urlEncoder.features.realTimePreview',
          'tools.urlEncoder.features.validationCheck'
        ],
        proTips: [
          'tools.urlEncoder.tips.encodeParameters',
          'tools.urlEncoder.tips.checkSpecialChars',
          'tools.urlEncoder.tips.testUrls',
          'tools.urlEncoder.tips.useForForms'
        ],
        usageGuide: [
          'tools.urlEncoder.guide.step1',
          'tools.urlEncoder.guide.step2',
          'tools.urlEncoder.guide.step3',
          'tools.urlEncoder.guide.step4',
          'tools.urlEncoder.guide.step5'
        ],
        faq: [
          {question: 'tools.urlEncoder.faq.q1', answer: 'tools.urlEncoder.faq.a1'},
          {question: 'tools.urlEncoder.faq.q2', answer: 'tools.urlEncoder.faq.a2'},
          {question: 'tools.urlEncoder.faq.q3', answer: 'tools.urlEncoder.faq.a3'},
          {question: 'tools.urlEncoder.faq.q4', answer: 'tools.urlEncoder.faq.a4'},
          {question: 'tools.urlEncoder.faq.q5', answer: 'tools.urlEncoder.faq.a5'}
        ],
        tutorial: {
          steps: [
            'tools.urlEncoder.tutorial.step1',
            'tools.urlEncoder.tutorial.step2',
            'tools.urlEncoder.tutorial.step3',
            'tools.urlEncoder.tutorial.step4'
          ],
          examples: [
            {
              title: 'tools.urlEncoder.examples.query.title',
              input: 'tools.urlEncoder.examples.query.input',
              output: 'tools.urlEncoder.examples.query.output'
            },
            {
              title: 'tools.urlEncoder.examples.special.title',
              input: 'tools.urlEncoder.examples.special.input',
              output: 'tools.urlEncoder.examples.special.output'
            }
          ]
        },
        seoKeywords: ['URL encoder online', 'URL decoder', 'encode URL', 'decode URL', 'URL encoding tool', 'percent encoding']
      },
      {
        id: 'uuid-generator',
        name: 'tools.uuidGenerator.name',
        description: 'tools.uuidGenerator.description',
        longDescription: 'tools.uuidGenerator.longDescription',
        icon: Key,
        category: 'developer',
        path: '/tools/developer/uuid-generator',
        featured: true,
        keywords: 'tools.uuidGenerator.keywords',
        useCases: [
          'tools.uuidGenerator.useCases.databaseKeys',
          'tools.uuidGenerator.useCases.apiIdentifiers',
          'tools.uuidGenerator.useCases.sessionTokens',
          'tools.uuidGenerator.useCases.fileNaming',
          'tools.uuidGenerator.useCases.distributedSystems'
        ],
        benefits: [
          'tools.uuidGenerator.benefits.globallyUnique',
          'tools.uuidGenerator.benefits.noCollisions',
          'tools.uuidGenerator.benefits.standardCompliant',
          'tools.uuidGenerator.benefits.crossPlatform'
        ],
        keyFeatures: [
          'tools.uuidGenerator.features.multipleVersions',
          'tools.uuidGenerator.features.bulkGeneration',
          'tools.uuidGenerator.features.copyToClipboard',
          'tools.uuidGenerator.features.formatOptions',
          'tools.uuidGenerator.features.validationCheck',
          'tools.uuidGenerator.features.exportOptions'
        ],
        proTips: [
          'tools.uuidGenerator.tips.useVersion4',
          'tools.uuidGenerator.tips.storeSecurely',
          'tools.uuidGenerator.tips.validateFormat',
          'tools.uuidGenerator.tips.considerContext'
        ],
        usageGuide: [
          'tools.uuidGenerator.usageGuide.0',
          'tools.uuidGenerator.usageGuide.1',
          'tools.uuidGenerator.usageGuide.2',
          'tools.uuidGenerator.usageGuide.3',
          'tools.uuidGenerator.usageGuide.4'
        ],
        faq: [
          {question: 'tools.uuidGenerator.faq.0.question', answer: 'tools.uuidGenerator.faq.0.answer'},
          {question: 'tools.uuidGenerator.faq.1.question', answer: 'tools.uuidGenerator.faq.1.answer'},
          {question: 'tools.uuidGenerator.faq.2.question', answer: 'tools.uuidGenerator.faq.2.answer'}
        ],
        tutorial: {
          steps: [
            'tools.uuidGenerator.tutorial.steps.0',
            'tools.uuidGenerator.tutorial.steps.1',
            'tools.uuidGenerator.tutorial.steps.2',
            'tools.uuidGenerator.tutorial.steps.3'
          ],
          examples: [
            {
              title: 'tools.uuidGenerator.tutorial.examples.0.title',
              input: 'tools.uuidGenerator.tutorial.examples.0.input',
              output: 'tools.uuidGenerator.tutorial.examples.0.output'
            },
            {
              title: 'tools.uuidGenerator.tutorial.examples.1.title',
              input: 'tools.uuidGenerator.tutorial.examples.1.input',
              output: 'tools.uuidGenerator.tutorial.examples.1.output'
            }
          ]
        }
      },
      {
        id: 'hash-generator',
        name: 'tools.hashGenerator.name',
        description: 'tools.hashGenerator.description',
        longDescription: 'tools.hashGenerator.longDescription',
        icon: Hash,
        category: 'developer',
        path: '/tools/developer/hash-generator',
        keywords: 'tools.hashGenerator.keywords',
        useCases: [
          'tools.hashGenerator.useCases.passwordHashing',
          'tools.hashGenerator.useCases.dataIntegrity',
          'tools.hashGenerator.useCases.fileVerification',
          'tools.hashGenerator.useCases.digitalSignatures',
          'tools.hashGenerator.useCases.cacheKeys'
        ],
        benefits: [
          'tools.hashGenerator.benefits.secureHashing',
          'tools.hashGenerator.benefits.multipleAlgorithms',
          'tools.hashGenerator.benefits.fastProcessing',
          'tools.hashGenerator.benefits.standardCompliant'
        ],
        keyFeatures: [
          'tools.hashGenerator.features.multipleAlgorithms',
          'tools.hashGenerator.features.fileHashing',
          'tools.hashGenerator.features.textHashing',
          'tools.hashGenerator.features.copyToClipboard',
          'tools.hashGenerator.features.batchProcessing',
          'tools.hashGenerator.features.hashComparison'
        ],
        proTips: [
          'tools.hashGenerator.tips.chooseSHA256',
          'tools.hashGenerator.tips.saltPasswords',
          'tools.hashGenerator.tips.verifyIntegrity',
          'tools.hashGenerator.tips.storeSecurely'
        ],
        usageGuide: [
          'tools.hashGenerator.guide.step1',
          'tools.hashGenerator.guide.step2',
          'tools.hashGenerator.guide.step3',
          'tools.hashGenerator.guide.step4',
          'tools.hashGenerator.guide.step5'
        ],
        faq: [
          {question: 'tools.hashGenerator.faq.q1', answer: 'tools.hashGenerator.faq.a1'},
          {question: 'tools.hashGenerator.faq.q2', answer: 'tools.hashGenerator.faq.a2'},
          {question: 'tools.hashGenerator.faq.q3', answer: 'tools.hashGenerator.faq.a3'},
          {question: 'tools.hashGenerator.faq.q4', answer: 'tools.hashGenerator.faq.a4'},
          {question: 'tools.hashGenerator.faq.q5', answer: 'tools.hashGenerator.faq.a5'}
        ],
        tutorial: {
          steps: [
            'tools.hashGenerator.tutorial.step1',
            'tools.hashGenerator.tutorial.step2',
            'tools.hashGenerator.tutorial.step3',
            'tools.hashGenerator.tutorial.step4'
          ],
          examples: [
            {
              title: 'tools.hashGenerator.examples.text.title',
              input: 'tools.hashGenerator.examples.text.input',
              output: 'tools.hashGenerator.examples.text.output'
            },
            {
              title: 'tools.hashGenerator.examples.file.title',
              input: 'tools.hashGenerator.examples.file.input',
              output: 'tools.hashGenerator.examples.file.output'
            }
          ]
        },
        usageGuide: [
          'tools.hashGenerator.guide.step1',
          'tools.hashGenerator.guide.step2',
          'tools.hashGenerator.guide.step3',
          'tools.hashGenerator.guide.step4',
          'tools.hashGenerator.guide.step5'
        ],
        faq: [
          {question: 'tools.hashGenerator.faq.q1', answer: 'tools.hashGenerator.faq.a1'},
          {question: 'tools.hashGenerator.faq.q2', answer: 'tools.hashGenerator.faq.a2'},
          {question: 'tools.hashGenerator.faq.q3', answer: 'tools.hashGenerator.faq.a3'},
          {question: 'tools.hashGenerator.faq.q4', answer: 'tools.hashGenerator.faq.a4'},
          {question: 'tools.hashGenerator.faq.q5', answer: 'tools.hashGenerator.faq.a5'}
        ],
        tutorial: {
          steps: [
            'tools.hashGenerator.tutorial.step1',
            'tools.hashGenerator.tutorial.step2',
            'tools.hashGenerator.tutorial.step3',
            'tools.hashGenerator.tutorial.step4'
          ],
          examples: [
            {
              title: 'tools.hashGenerator.examples.text.title',
              input: 'tools.hashGenerator.examples.text.input',
              output: 'tools.hashGenerator.examples.text.output'
            },
            {
              title: 'tools.hashGenerator.examples.file.title',
              input: 'tools.hashGenerator.examples.file.input',
              output: 'tools.hashGenerator.examples.file.output'
            }
          ]
        },
        seoKeywords: ['hash generator online', 'MD5 generator', 'SHA256 generator', 'hash calculator', 'checksum generator', 'file hash']
      },
      {
        id: 'regex-tester',
        name: 'tools.regexTester.name',
        description: 'tools.regexTester.description',
        longDescription: 'tools.regexTester.longDescription',
        icon: Search,
        category: 'developer',
        path: '/tools/developer/regex-tester',
        keywords: 'tools.regexTester.keywords',
        useCases: 'tools.regexTester.useCases',
        benefits: 'tools.regexTester.benefits',
      },
      {
        id: 'html-encoder',
        name: 'tools.htmlEncoder.name',
        description: 'tools.htmlEncoder.description',
        longDescription: 'tools.htmlEncoder.longDescription',
        icon: Code,
        category: 'developer',
        path: '/tools/developer/html-encoder',
        keywords: 'tools.htmlEncoder.keywords',
        useCases: 'tools.htmlEncoder.useCases',
        benefits: 'tools.htmlEncoder.benefits',
      },
      {
        id: 'css-minifier',
        name: 'tools.cssMinifier.name',
        description: 'tools.cssMinifier.description',
        longDescription: 'tools.cssMinifier.longDescription',
        icon: Layers,
        category: 'developer',
        path: '/tools/developer/css-minifier',
        keywords: 'tools.cssMinifier.keywords',
        useCases: 'tools.cssMinifier.useCases',
        benefits: 'tools.cssMinifier.benefits',
      },
      {
        id: 'js-minifier',
        name: 'tools.jsMinifier.name',
        description: 'tools.jsMinifier.description',
        longDescription: 'tools.jsMinifier.longDescription',
        icon: FileCode2,
        category: 'developer',
        path: '/tools/developer/js-minifier',
        keywords: 'tools.jsMinifier.keywords',
        useCases: 'tools.jsMinifier.useCases',
        benefits: 'tools.jsMinifier.benefits',
      },
    ],
  },
  // 编码转换
  {
    id: 'converter',
    name: 'categories.converter',
    description: 'categories.converter.description',
    icon: Shuffle,
    color: 'green',
    tools: [
      {
        id: 'color-converter',
        name: 'tools.colorConverter.name',
        description: 'tools.colorConverter.description',
        longDescription: 'tools.colorConverter.longDescription',
        icon: Palette,
        category: 'converter',
        path: '/tools/converter/color-converter',
        featured: true,
        keywords: 'tools.colorConverter.keywords',
        useCases: 'tools.colorConverter.useCases',
        benefits: 'tools.colorConverter.benefits',
        usageGuide: [
          'tools.colorConverter.guide.step1',
          'tools.colorConverter.guide.step2',
          'tools.colorConverter.guide.step3',
          'tools.colorConverter.guide.step4',
          'tools.colorConverter.guide.step5'
        ],
        faq: [
          {question: 'tools.colorConverter.faq.q1', answer: 'tools.colorConverter.faq.a1'},
          {question: 'tools.colorConverter.faq.q2', answer: 'tools.colorConverter.faq.a2'},
          {question: 'tools.colorConverter.faq.q3', answer: 'tools.colorConverter.faq.a3'},
          {question: 'tools.colorConverter.faq.q4', answer: 'tools.colorConverter.faq.a4'},
          {question: 'tools.colorConverter.faq.q5', answer: 'tools.colorConverter.faq.a5'}
        ],
        tutorial: {
          steps: [
            'tools.colorConverter.tutorial.step1',
            'tools.colorConverter.tutorial.step2',
            'tools.colorConverter.tutorial.step3',
            'tools.colorConverter.tutorial.step4'
          ],
          examples: [
            {
              title: 'tools.colorConverter.examples.basic.title',
              input: 'tools.colorConverter.examples.basic.input',
              output: 'tools.colorConverter.examples.basic.output'
            },
            {
              title: 'tools.colorConverter.examples.advanced.title',
              input: 'tools.colorConverter.examples.advanced.input',
              output: 'tools.colorConverter.examples.advanced.output'
            }
          ]
        },
        seoKeywords: ['color converter online', 'HEX to RGB', 'RGB to HEX', 'color picker', 'color palette', 'color codes']
      },
      {
        id: 'timestamp-converter',
        name: 'tools.timestampConverter.name',
        description: 'tools.timestampConverter.description',
        longDescription: 'tools.timestampConverter.longDescription',
        icon: Clock,
        category: 'converter',
        path: '/tools/converter/timestamp-converter',
        featured: true,
        keywords: 'tools.timestampConverter.keywords',
        useCases: 'tools.timestampConverter.useCases',
        benefits: 'tools.timestampConverter.benefits',
      },
      {
        id: 'qr-generator',
        name: 'tools.qrGenerator.name',
        description: 'tools.qrGenerator.description',
        longDescription: 'tools.qrGenerator.longDescription',
        icon: QrCode,
        category: 'converter',
        path: '/tools/converter/qr-generator',
        keywords: 'tools.qrGenerator.keywords',
        useCases: 'tools.qrGenerator.useCases',
        benefits: 'tools.qrGenerator.benefits',
        usageGuide: [
          'tools.qrGenerator.guide.step1',
          'tools.qrGenerator.guide.step2',
          'tools.qrGenerator.guide.step3',
          'tools.qrGenerator.guide.step4',
          'tools.qrGenerator.guide.step5'
        ],
        faq: [
          {question: 'tools.qrGenerator.faq.q1', answer: 'tools.qrGenerator.faq.a1'},
          {question: 'tools.qrGenerator.faq.q2', answer: 'tools.qrGenerator.faq.a2'},
          {question: 'tools.qrGenerator.faq.q3', answer: 'tools.qrGenerator.faq.a3'},
          {question: 'tools.qrGenerator.faq.q4', answer: 'tools.qrGenerator.faq.a4'},
          {question: 'tools.qrGenerator.faq.q5', answer: 'tools.qrGenerator.faq.a5'}
        ],
        tutorial: {
          steps: [
            'tools.qrGenerator.tutorial.step1',
            'tools.qrGenerator.tutorial.step2',
            'tools.qrGenerator.tutorial.step3',
            'tools.qrGenerator.tutorial.step4'
          ],
          examples: [
            {
              title: 'tools.qrGenerator.examples.url.title',
              input: 'tools.qrGenerator.examples.url.input',
              output: 'tools.qrGenerator.examples.url.output'
            },
            {
              title: 'tools.qrGenerator.examples.text.title',
              input: 'tools.qrGenerator.examples.text.input',
              output: 'tools.qrGenerator.examples.text.output'
            }
          ]
        },
        seoKeywords: ['QR code generator online', 'create QR code', 'QR code maker', 'generate QR code', 'free QR generator', 'QR code creator']
      },
      {
        id: 'unit-converter',
        name: 'tools.unitConverter.name',
        description: 'tools.unitConverter.description',
        longDescription: 'tools.unitConverter.longDescription',
        icon: BarChart3,
        category: 'converter',
        path: '/tools/converter/unit-converter',
        keywords: 'tools.unitConverter.keywords',
        useCases: 'tools.unitConverter.useCases',
        benefits: 'tools.unitConverter.benefits',
      },
      {
        id: 'base-converter',
        name: 'tools.baseConverter.name',
        description: 'tools.baseConverter.description',
        longDescription: 'tools.baseConverter.longDescription',
        icon: Hash,
        category: 'converter',
        path: '/tools/converter/base-converter',
        keywords: 'tools.baseConverter.keywords',
        useCases: 'tools.baseConverter.useCases',
        benefits: 'tools.baseConverter.benefits',
      },
    ],
  },
  // 文本处理
  {
    id: 'text',
    name: 'categories.text',
    description: 'categories.text.description',
    icon: FileText,
    color: 'orange',
    tools: [
      {
        id: 'markdown-editor',
        name: 'tools.markdownEditor.name',
        description: 'tools.markdownEditor.description',
        longDescription: 'tools.markdownEditor.longDescription',
        icon: Edit,
        category: 'text',
        path: '/tools/text/markdown-editor',
        featured: true,
        keywords: 'tools.markdownEditor.keywords',
        useCases: 'tools.markdownEditor.useCases',
        benefits: 'tools.markdownEditor.benefits',
      },
      {
        id: 'text-diff',
        name: 'tools.textDiff.name',
        description: 'tools.textDiff.description',
        longDescription: 'tools.textDiff.longDescription',
        icon: GitCompare,
        category: 'text',
        path: '/tools/text/text-diff',
        keywords: 'tools.textDiff.keywords',
        useCases: 'tools.textDiff.useCases',
        benefits: 'tools.textDiff.benefits',
      },
      {
        id: 'password-generator',
        name: 'tools.passwordGenerator.name',
        description: 'tools.passwordGenerator.description',
        longDescription: 'tools.passwordGenerator.longDescription',
        icon: Lock,
        category: 'text',
        path: '/tools/text/password-generator',
        keywords: 'tools.passwordGenerator.keywords',
        useCases: 'tools.passwordGenerator.useCases',
        benefits: 'tools.passwordGenerator.benefits',
        usageGuide: [
          'tools.passwordGenerator.guide.step1',
          'tools.passwordGenerator.guide.step2',
          'tools.passwordGenerator.guide.step3',
          'tools.passwordGenerator.guide.step4',
          'tools.passwordGenerator.guide.step5'
        ],
        faq: [
          {question: 'tools.passwordGenerator.faq.q1', answer: 'tools.passwordGenerator.faq.a1'},
          {question: 'tools.passwordGenerator.faq.q2', answer: 'tools.passwordGenerator.faq.a2'},
          {question: 'tools.passwordGenerator.faq.q3', answer: 'tools.passwordGenerator.faq.a3'},
          {question: 'tools.passwordGenerator.faq.q4', answer: 'tools.passwordGenerator.faq.a4'},
          {question: 'tools.passwordGenerator.faq.q5', answer: 'tools.passwordGenerator.faq.a5'}
        ],
        tutorial: {
          steps: [
            'tools.passwordGenerator.tutorial.step1',
            'tools.passwordGenerator.tutorial.step2',
            'tools.passwordGenerator.tutorial.step3',
            'tools.passwordGenerator.tutorial.step4'
          ],
          examples: [
            {
              title: 'tools.passwordGenerator.examples.strong.title',
              input: 'tools.passwordGenerator.examples.strong.input',
              output: 'tools.passwordGenerator.examples.strong.output'
            },
            {
              title: 'tools.passwordGenerator.examples.memorable.title',
              input: 'tools.passwordGenerator.examples.memorable.input',
              output: 'tools.passwordGenerator.examples.memorable.output'
            }
          ]
        },
        seoKeywords: ['password generator online', 'strong password generator', 'secure password creator', 'random password generator', 'password maker', 'generate password']
      },
      {
        id: 'text-case-converter',
        name: 'tools.textCaseConverter.name',
        description: 'tools.textCaseConverter.description',
        longDescription: 'tools.textCaseConverter.longDescription',
        icon: Type,
        category: 'text',
        path: '/tools/text/text-case-converter',
        keywords: 'tools.textCaseConverter.keywords',
        useCases: 'tools.textCaseConverter.useCases',
        benefits: 'tools.textCaseConverter.benefits',
      },
      {
        id: 'word-counter',
        name: 'tools.wordCounter.name',
        description: 'tools.wordCounter.description',
        longDescription: 'tools.wordCounter.longDescription',
        icon: BarChart3,
        category: 'text',
        path: '/tools/text/word-counter',
        keywords: 'tools.wordCounter.keywords',
        useCases: 'tools.wordCounter.useCases',
        benefits: 'tools.wordCounter.benefits',
      },
      {
        id: 'lorem-generator',
        name: 'tools.loremGenerator.name',
        description: 'tools.loremGenerator.description',
        longDescription: 'tools.loremGenerator.longDescription',
        icon: FileText,
        category: 'text',
        path: '/tools/text/lorem-generator',
        keywords: 'tools.loremGenerator.keywords',
        useCases: 'tools.loremGenerator.useCases',
        benefits: 'tools.loremGenerator.benefits',
      },
      {
        id: 'text-to-speech',
        name: 'tools.textToSpeech.name',
        description: 'tools.textToSpeech.description',
        longDescription: 'tools.textToSpeech.longDescription',
        icon: Volume2,
        category: 'text',
        path: '/tools/text/text-to-speech',
        keywords: 'tools.textToSpeech.keywords',
        useCases: 'tools.textToSpeech.useCases',
        benefits: 'tools.textToSpeech.benefits',
      },
    ],
  },
  // 图像工具
  {
    id: 'image',
    name: 'categories.image',
    description: 'categories.image.description',
    icon: Image,
    color: 'pink',
    tools: [
      {
        id: 'image-watermark',
        name: 'tools.imageWatermark.name',
        description: 'tools.imageWatermark.description',
        longDescription: 'tools.imageWatermark.longDescription',
        icon: Camera,
        category: 'image',
        path: '/tools/image/image-watermark',
        featured: true,
        keywords: 'tools.imageWatermark.keywords',
        useCases: 'tools.imageWatermark.useCases',
        benefits: 'tools.imageWatermark.benefits',
      },
      {
        id: 'image-compress',
        name: 'tools.imageCompress.name',
        description: 'tools.imageCompress.description',
        longDescription: 'tools.imageCompress.longDescription',
        icon: Archive,
        category: 'image',
        path: '/tools/image/image-compress',
        keywords: 'tools.imageCompress.keywords',
        useCases: 'tools.imageCompress.useCases',
        benefits: 'tools.imageCompress.benefits',
      },
      {
        id: 'image-format-converter',
        name: 'tools.imageFormatConverter.name',
        description: 'tools.imageFormatConverter.description',
        longDescription: 'tools.imageFormatConverter.longDescription',
        icon: Shuffle,
        category: 'image',
        path: '/tools/image/image-format-converter',
        keywords: 'tools.imageFormatConverter.keywords',
        useCases: 'tools.imageFormatConverter.useCases',
        benefits: 'tools.imageFormatConverter.benefits',
      },
      {
        id: 'image-crop',
        name: 'tools.imageCrop.name',
        description: 'tools.imageCrop.description',
        longDescription: 'tools.imageCrop.longDescription',
        icon: Crop,
        category: 'image',
        path: '/tools/image/image-crop',
        keywords: 'tools.imageCrop.keywords',
        useCases: 'tools.imageCrop.useCases',
        benefits: 'tools.imageCrop.benefits',
      },
      {
        id: 'image-resize',
        name: 'tools.imageResize.name',
        description: 'tools.imageResize.description',
        longDescription: 'tools.imageResize.longDescription',
        icon: Minimize2,
        category: 'image',
        path: '/tools/image/image-resize',
        keywords: 'tools.imageResize.keywords',
        useCases: 'tools.imageResize.useCases',
        benefits: 'tools.imageResize.benefits',
      },
      {
        id: 'image-color-picker',
        name: 'tools.imageColorPicker.name',
        description: 'tools.imageColorPicker.description',
        longDescription: 'tools.imageColorPicker.longDescription',
        icon: Droplets,
        category: 'image',
        path: '/tools/image/image-color-picker',
        keywords: 'tools.imageColorPicker.keywords',
        useCases: 'tools.imageColorPicker.useCases',
        benefits: 'tools.imageColorPicker.benefits',
      },
    ],
  },
  // 网络工具
  {
    id: 'network',
    name: 'categories.network',
    description: 'categories.network.description',
    icon: Globe,
    color: 'purple',
    tools: [
      {
        id: 'whois-lookup',
        name: 'tools.whoisLookup.name',
        description: 'tools.whoisLookup.description',
        longDescription: 'tools.whoisLookup.longDescription',
        icon: Search,
        category: 'network',
        path: '/tools/network/whois-lookup',
        keywords: 'tools.whoisLookup.keywords',
        useCases: 'tools.whoisLookup.useCases',
        benefits: 'tools.whoisLookup.benefits',
      },
      {
        id: 'ip-lookup',
        name: 'tools.ipLookup.name',
        description: 'tools.ipLookup.description',
        longDescription: 'tools.ipLookup.longDescription',
        icon: MapPin,
        category: 'network',
        path: '/tools/network/ip-lookup',
        featured: true,
        keywords: 'tools.ipLookup.keywords',
        useCases: 'tools.ipLookup.useCases',
        benefits: 'tools.ipLookup.benefits',
      },
      {
        id: 'dns-lookup',
        name: 'tools.dnsLookup.name',
        description: 'tools.dnsLookup.description',
        longDescription: 'tools.dnsLookup.longDescription',
        icon: Server,
        category: 'network',
        path: '/tools/network/dns-lookup',
        keywords: 'tools.dnsLookup.keywords',
        useCases: 'tools.dnsLookup.useCases',
        benefits: 'tools.dnsLookup.benefits',
      },
      {
        id: 'ping-test',
        name: 'tools.pingTest.name',
        description: 'tools.pingTest.description',
        longDescription: 'tools.pingTest.longDescription',
        icon: Activity,
        category: 'network',
        path: '/tools/network/ping-test',
        keywords: 'tools.pingTest.keywords',
        useCases: 'tools.pingTest.useCases',
        benefits: 'tools.pingTest.benefits',
      },
      {
        id: 'port-scanner',
        name: 'tools.portScanner.name',
        description: 'tools.portScanner.description',
        longDescription: 'tools.portScanner.longDescription',
        icon: Network,
        category: 'network',
        path: '/tools/network/port-scanner',
        keywords: 'tools.portScanner.keywords',
        useCases: 'tools.portScanner.useCases',
        benefits: 'tools.portScanner.benefits',
      },
    ],
  },

  // 数学计算
  {
    id: 'math',
    name: 'categories.math',
    description: 'categories.math.description',
    icon: Calculator,
    color: 'indigo',
    tools: [
      {
        id: 'calculator',
        name: 'tools.calculator.name',
        description: 'tools.calculator.description',
        longDescription: 'tools.calculator.longDescription',
        icon: Calculator,
        category: 'math',
        path: '/tools/math/calculator',
        featured: true,
        keywords: 'tools.calculator.keywords',
        useCases: 'tools.calculator.useCases',
        benefits: 'tools.calculator.benefits',
      },
      {
        id: 'percentage-calculator',
        name: 'tools.percentageCalculator.name',
        description: 'tools.percentageCalculator.description',
        longDescription: 'tools.percentageCalculator.longDescription',
        icon: Percent,
        category: 'math',
        path: '/tools/math/percentage-calculator',
        keywords: 'tools.percentageCalculator.keywords',
        useCases: 'tools.percentageCalculator.useCases',
        benefits: 'tools.percentageCalculator.benefits',
      },
      {
        id: 'random-generator',
        name: 'tools.randomGenerator.name',
        description: 'tools.randomGenerator.description',
        longDescription: 'tools.randomGenerator.longDescription',
        icon: Dices,
        category: 'math',
        path: '/tools/math/random-generator',
        keywords: 'tools.randomGenerator.keywords',
        useCases: 'tools.randomGenerator.useCases',
        benefits: 'tools.randomGenerator.benefits',
      },
    ],
  },

  // 日期时间
  {
    id: 'datetime',
    name: 'categories.datetime',
    description: 'categories.datetime.description',
    icon: Calendar,
    color: 'teal',
    tools: [
      {
        id: 'date-calculator',
        name: 'tools.dateCalculator.name',
        description: 'tools.dateCalculator.description',
        longDescription: 'tools.dateCalculator.longDescription',
        icon: Calendar,
        category: 'datetime',
        path: '/tools/datetime/date-calculator',
        keywords: 'tools.dateCalculator.keywords',
        useCases: 'tools.dateCalculator.useCases',
        benefits: 'tools.dateCalculator.benefits',
      },
      {
        id: 'timezone-converter',
        name: 'tools.timezoneConverter.name',
        description: 'tools.timezoneConverter.description',
        longDescription: 'tools.timezoneConverter.longDescription',
        icon: Globe,
        category: 'datetime',
        path: '/tools/datetime/timezone-converter',
        keywords: 'tools.timezoneConverter.keywords',
        useCases: 'tools.timezoneConverter.useCases',
        benefits: 'tools.timezoneConverter.benefits',
      },
      {
        id: 'countdown-timer',
        name: 'tools.countdownTimer.name',
        description: 'tools.countdownTimer.description',
        longDescription: 'tools.countdownTimer.longDescription',
        icon: Timer,
        category: 'datetime',
        path: '/tools/datetime/countdown-timer',
        keywords: 'tools.countdownTimer.keywords',
        useCases: 'tools.countdownTimer.useCases',
        benefits: 'tools.countdownTimer.benefits',
      },
      {
        id: 'age-calculator',
        name: 'tools.ageCalculator.name',
        description: 'tools.ageCalculator.description',
        longDescription: 'tools.ageCalculator.longDescription',
        icon: Baby,
        category: 'datetime',
        path: '/tools/datetime/age-calculator',
        keywords: 'tools.ageCalculator.keywords',
        useCases: 'tools.ageCalculator.useCases',
        benefits: 'tools.ageCalculator.benefits',
      },
      {
        id: 'working-days-calculator',
        name: 'tools.workingDaysCalculator.name',
        description: 'tools.workingDaysCalculator.description',
        longDescription: 'tools.workingDaysCalculator.longDescription',
        icon: Briefcase,
        category: 'datetime',
        path: '/tools/datetime/working-days-calculator',
        keywords: 'tools.workingDaysCalculator.keywords',
        useCases: 'tools.workingDaysCalculator.useCases',
        benefits: 'tools.workingDaysCalculator.benefits',
      },
    ],
  },

  // 数据处理
  {
    id: 'data',
    name: 'categories.data',
    description: 'categories.data.description',
    icon: Database,
    color: 'cyan',
    tools: [
      {
        id: 'csv-to-json',
        name: 'tools.csvToJson.name',
        description: 'tools.csvToJson.description',
        longDescription: 'tools.csvToJson.longDescription',
        icon: FileSpreadsheet,
        category: 'data',
        path: '/tools/data/csv-to-json',
        keywords: 'tools.csvToJson.keywords',
        useCases: 'tools.csvToJson.useCases',
        benefits: 'tools.csvToJson.benefits',
      },
      {
        id: 'json-to-csv',
        name: 'tools.jsonToCsv.name',
        description: 'tools.jsonToCsv.description',
        longDescription: 'tools.jsonToCsv.longDescription',
        icon: FileCode,
        category: 'data',
        path: '/tools/data/json-to-csv',
        keywords: 'tools.jsonToCsv.keywords',
        useCases: 'tools.jsonToCsv.useCases',
        benefits: 'tools.jsonToCsv.benefits',
      },
      {
        id: 'xml-formatter',
        name: 'tools.xmlFormatter.name',
        description: 'tools.xmlFormatter.description',
        longDescription: 'tools.xmlFormatter.longDescription',
        icon: Code,
        category: 'data',
        path: '/tools/data/xml-formatter',
        keywords: 'tools.xmlFormatter.keywords',
        useCases: 'tools.xmlFormatter.useCases',
        benefits: 'tools.xmlFormatter.benefits',
      },
      {
        id: 'sql-formatter',
        name: 'tools.sqlFormatter.name',
        description: 'tools.sqlFormatter.description',
        longDescription: 'tools.sqlFormatter.longDescription',
        icon: Database,
        category: 'data',
        path: '/tools/data/sql-formatter',
        keywords: 'tools.sqlFormatter.keywords',
        useCases: 'tools.sqlFormatter.useCases',
        benefits: 'tools.sqlFormatter.benefits',
      },
    ],
  },

  // 安全工具
  {
    id: 'security',
    name: 'categories.security',
    description: 'categories.security.description',
    icon: Shield,
    color: 'red',
    tools: [
      {
        id: 'password-strength-checker',
        name: 'tools.passwordStrengthChecker.name',
        description: 'tools.passwordStrengthChecker.description',
        longDescription: 'tools.passwordStrengthChecker.longDescription',
        icon: Lock,
        category: 'security',
        path: '/tools/security/password-strength-checker',
        keywords: 'tools.passwordStrengthChecker.keywords',
        useCases: 'tools.passwordStrengthChecker.useCases',
        benefits: 'tools.passwordStrengthChecker.benefits',
      },
      {
        id: 'jwt-decoder',
        name: 'tools.jwtDecoder.name',
        description: 'tools.jwtDecoder.description',
        longDescription: 'tools.jwtDecoder.longDescription',
        icon: Key,
        category: 'security',
        path: '/tools/security/jwt-decoder',
        keywords: 'tools.jwtDecoder.keywords',
        useCases: 'tools.jwtDecoder.useCases',
        benefits: 'tools.jwtDecoder.benefits',
        usageGuide: [
          'tools.jwtDecoder.guide.step1',
          'tools.jwtDecoder.guide.step2',
          'tools.jwtDecoder.guide.step3',
          'tools.jwtDecoder.guide.step4',
          'tools.jwtDecoder.guide.step5'
        ],
        faq: [
          {question: 'tools.jwtDecoder.faq.q1', answer: 'tools.jwtDecoder.faq.a1'},
          {question: 'tools.jwtDecoder.faq.q2', answer: 'tools.jwtDecoder.faq.a2'},
          {question: 'tools.jwtDecoder.faq.q3', answer: 'tools.jwtDecoder.faq.a3'},
          {question: 'tools.jwtDecoder.faq.q4', answer: 'tools.jwtDecoder.faq.a4'},
          {question: 'tools.jwtDecoder.faq.q5', answer: 'tools.jwtDecoder.faq.a5'}
        ],
        tutorial: {
          steps: [
            'tools.jwtDecoder.tutorial.step1',
            'tools.jwtDecoder.tutorial.step2',
            'tools.jwtDecoder.tutorial.step3',
            'tools.jwtDecoder.tutorial.step4'
          ],
          examples: [
            {
              title: 'tools.jwtDecoder.examples.basic.title',
              input: 'tools.jwtDecoder.examples.basic.input',
              output: 'tools.jwtDecoder.examples.basic.output'
            },
            {
              title: 'tools.jwtDecoder.examples.expired.title',
              input: 'tools.jwtDecoder.examples.expired.input',
              output: 'tools.jwtDecoder.examples.expired.output'
            }
          ]
        },
        seoKeywords: ['JWT decoder online', 'JSON Web Token decoder', 'JWT parser', 'decode JWT', 'JWT analyzer', 'token decoder']
      },
      {
        id: 'ssl-certificate-analyzer',
        name: 'tools.sslCertificateAnalyzer.name',
        description: 'tools.sslCertificateAnalyzer.description',
        longDescription: 'tools.sslCertificateAnalyzer.longDescription',
        icon: Shield,
        category: 'security',
        path: '/tools/security/ssl-certificate-analyzer',
        keywords: 'tools.sslCertificateAnalyzer.keywords',
        useCases: 'tools.sslCertificateAnalyzer.useCases',
        benefits: 'tools.sslCertificateAnalyzer.benefits',
      },
    ],
  },
];

export const getAllTools = (): Tool[] => {
  return categories.flatMap(category => category.tools);
};

export const getFeaturedTools = (): Tool[] => {
  return getAllTools().filter(tool => tool.featured);
};

export const getToolsByCategory = (categoryId: string): Tool[] => {
  const category = categories.find(cat => cat.id === categoryId);
  return category ? category.tools : [];
};

export const getToolById = (toolId: string): Tool | undefined => {
  return getAllTools().find(tool => tool.id === toolId);
};

export const getCategoryById = (categoryId: string): Category | undefined => {
  return categories.find(category => category.id === categoryId);
};
